import {DateTime} from 'luxon'

import {SUBSCRIPTION_DURATION_TYPE_MAP, SUBSCRIPTION_PRICE_TYPE_MAP} from '../../api/subscriptions/constants'
import {
  PriceOption,
  Subscription,
  SubscriptionData,
  SubscriptionDurationTypeEnum,
  SubscriptionPriceTypesEnum,
} from '../../api/subscriptions/types'
import {safeRound} from '../../helpers/logic'
import {TranslatorScope} from '../../locale/types'
import {CurrenciesEnum} from '../../types/currencies'
import {l} from '../../utils/i18n'
import {addDateTime, getDateRange} from '../../utils/time'


type InstOptionsParams = {
  totalPrice: number,
  currency: CurrenciesEnum,
  maxInstallments: number,
}

type InstOptionsReturnType = {
  numberOfPayments: number
  nextPaymentsPrice: number
  firstPaymentPrice: number
  value: number
  description: string | null
  label: string
} | {
  value: null
  label: string
}

export const generateSubscriptionInstallmentsOptions = ({
  totalPrice,
  currency,
  maxInstallments,
} : InstOptionsParams
): InstOptionsReturnType[] => {
  const options = new Array(maxInstallments).fill(null)
  const temp = options.map((_, index) => {
    const multipliedPrice = totalPrice * 100
    const rest = multipliedPrice % (index + 1)
    const commonPrice = (multipliedPrice - rest) / (index + 1)
    const nextPaymentsPrice = commonPrice / 100
    const firstPaymentPrice = safeRound(commonPrice + rest) / 100
    const monthsAmountLabel = l('months', {
      value: index,
      declensionKey: true,
    })
    return {
      numberOfPayments: index,
      nextPaymentsPrice,
      firstPaymentPrice,
      description: index > 0
        ? l('installments_description', {
          payAmount: monthsAmountLabel,
          othersPay: nextPaymentsPrice,
          firstPay: firstPaymentPrice
        })
        : null,
      value: index,
      label: l('next_payment_total', {
        index: l('payments', {
          value: index + 1,
          declensionKey: true,
          caseAction: 'lowerCase'
        }),
        price: nextPaymentsPrice
      })
    }
  })

  console.log('====> ~ file: helpers.ts:43 ~ temp:', temp)
  return [
    {
      value: null,
      label: l('none')
    },
    ...temp
  ]
}

type Price = {
  type: number;
  price: number;
}

export const getAdditionalInstallmentsData = (curInstallment: InstOptionsReturnType) => {
  console.log('====> ~ file: helpers.ts:91 ~ getAdditionalInstallmentsData ~ curInstallment:', curInstallment)
  if ('numberOfPayments' in curInstallment) {
    return (
      {
        cred_type: 8,
        npay: curInstallment.numberOfPayments,
        fpay: curInstallment.firstPaymentPrice,
        spay: curInstallment.nextPaymentsPrice,
        installmentAmount: curInstallment.numberOfPayments + 1,
      }
    )
  }
  return null
}

export const calculateSubscriptionStartDate = ({
  rolling,
  startDateType,
  specificStartDate,
  fromDateOfPurchaseAfterStartDate,
  startDate: selectedMembershipStartDate,
}: {
  rolling: boolean
  startDate?: DateTime
  specificStartDate?: string
  startDateType: 'PURCHASE_DATE' | 'SPECIFIC_DATE' | 'USER_SELECTED_DATE'
  fromDateOfPurchaseAfterStartDate: boolean
}) => {
  const currentDate = DateTime.utc().startOf('day')
  let startDate = selectedMembershipStartDate || currentDate

  if (startDateType === 'PURCHASE_DATE') {
    startDate = DateTime.utc().startOf('day')
  }

  if (startDateType === 'SPECIFIC_DATE' && specificStartDate) {
    startDate = DateTime.fromFormat(specificStartDate, 'dd/MM/yyyy').startOf('day') as DateTime<true>
  }

  if (startDate.toMillis() < currentDate.toMillis() && fromDateOfPurchaseAfterStartDate) {
    startDate = DateTime.utc().startOf('day')
  }

  if (rolling && !fromDateOfPurchaseAfterStartDate && startDate.toMillis() < currentDate.toMillis()) {
    const weekDay = startDate.day
    let expectedDate = DateTime.utc().set({day: weekDay}).startOf('day')

    if (expectedDate.toMillis() > currentDate.toMillis()) {
      expectedDate = DateTime.utc().minus({months: 1}).set({day: weekDay}).startOf('day')
    }
    startDate = expectedDate
  }

  return startDate
}

export const generateOptionPerType = ({
  rolling,
  priceTime,
  prices = [],
  // timeZone,
  startDateType,
  specificStartDate,
  durationFromSubscription,
  durationTypeFromSubscription,
  fromDateOfPurchaseAfterStartDate,
  startDate: selectedSubscriptionStartDate,
}: {
  rolling: boolean;
  prices?: Price[];
  timeZone?: string
  priceTime: number;
  startDate: DateTime;
  specificStartDate?: string;
  startDateType: 'PURCHASE_DATE' | 'SPECIFIC_DATE' | 'USER_SELECTED_DATE';
  durationFromSubscription: number;
  durationTypeFromSubscription: SubscriptionDurationTypeEnum;
  fromDateOfPurchaseAfterStartDate: boolean;
}) => {
  const startDate = calculateSubscriptionStartDate({
    rolling,
    // timeZone,
    startDateType,
    specificStartDate,
    fromDateOfPurchaseAfterStartDate,
    startDate: selectedSubscriptionStartDate,
  })

  const indexedPrices = prices.length > 0 ? Object.fromEntries(prices.map((p) => [p.type, p])) : {}
  let durationType = durationTypeFromSubscription
  let duration = durationFromSubscription

  if (
    priceTime === SubscriptionPriceTypesEnum.MONTHLY
    && durationTypeFromSubscription === SubscriptionDurationTypeEnum.YEARS
  ) {
    duration = durationFromSubscription * 12
    durationType = SubscriptionDurationTypeEnum.MONTHS
  }
  const price = indexedPrices[priceTime].price || 0
  let preCycles = Array.from({length: duration}, () => ({
    price,
    duration: 1,
    durationType,
  }))

  if (
    durationTypeFromSubscription === SubscriptionDurationTypeEnum.MONTHS
    && priceTime === SubscriptionPriceTypesEnum.YEARLY
    && duration >= 12
  ) {
    const yearPrice = indexedPrices[SubscriptionPriceTypesEnum.YEARLY].price || 0
    const monthPrice =
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      indexedPrices[SubscriptionPriceTypesEnum.MONTHLY]?.price || price - 1
    const yearsOptionsCount = duration / 12
    const monthOptionsCount = yearsOptionsCount - Math.floor(yearsOptionsCount)

    const repeatYears = Array.from({length: Math.floor(yearsOptionsCount)}, () => ({
      price: yearPrice,
      durationType,
      duration: 12,
    }))

    const repeatMonth = Array.from(
      {length: Math.round(monthOptionsCount * 12)},
      () => ({
        price: monthPrice,
        durationType,
        duration: 1,
      })
    )

    preCycles = [...repeatYears, ...repeatMonth]
  }

  if (priceTime === SubscriptionPriceTypesEnum.ONE_TIME) {
    preCycles = [
      {
        price,
        duration,
        durationType,
      },
    ]
  }

  const cycles: Cycle[] = []
  const currentDate = DateTime.utc().startOf('day').toJSDate()
  preCycles.reduce(
    (prev, el, index) => {
      const newStartDate = new Date(prev.endDate || startDate.valueOf())
      const newEndDate = addDateTime(
        newStartDate,
        el.duration,
        SUBSCRIPTION_DURATION_TYPE_MAP[el.durationType].dateFormatKey
      )
      const diffDaysAmount = getDateRange(newStartDate, newEndDate)
      const diffDaysForPayAccordingNow = getDateRange(currentDate, newEndDate)
      const isPastCycle = currentDate > newEndDate
      const IsCurrentCycle = currentDate >= newStartDate && currentDate < newEndDate
      const pricePercentForPay = isPastCycle
        ? 0
        : IsCurrentCycle
          ? (diffDaysForPayAccordingNow * 100) / diffDaysAmount
          : 100

      const calculatedPrice = (el.price * pricePercentForPay) / 100

      const data = {
        ...el,
        isPaid: false,
        elIndex: index + 1,
        diffDaysForPayAccordingNow,
        endDate: newEndDate.valueOf(),
        startDate: newStartDate.valueOf(),
        payDate: IsCurrentCycle ? currentDate.valueOf() : newStartDate.valueOf(),
        price: safeRound(calculatedPrice),
        isFullMonth: pricePercentForPay === 100,
        toPay: isPastCycle || IsCurrentCycle || index === 0,
        disabled: isPastCycle || IsCurrentCycle || index === 0,
      }
      cycles.push(data)
      return data
    },
    {endDate: 0, elIndex: 0} // аналог defaultCycleOption
  )

  return cycles
}

type Cycle = {
  price: number
  toPay: boolean
  disabled: boolean
  isFullMonth: boolean
  startDate: number
  endDate: number
  diffDaysForPayAccordingNow: unknown
}

export const getPayAmountWithSelectedCycles = (cycles: Cycle[], plan: PriceOption) => {
  return cycles.reduce((acc, el) => {
      if (el.toPay) {
        return {
          ...acc,
          selectedCycles: [...acc.selectedCycles, el],
          totalPrice: safeRound(acc.totalPrice + el.price),
        }
      }
      return acc
    },
    {
      selectedCycles: [] as Cycle[],
      availableCyclesAmount: cycles.length,
      totalPrice: plan.additionalFee || 0,
    }
  )
}

export type CycleOption = {
  plan: SubscriptionData['prices'][number],
  cycles: Cycle[],
  type: SubscriptionData['prices'][number]['type'],
  totalPrice: number,
  selectedCycles: Cycle[],
  availableCyclesAmount: number,
  installmentsOptions: InstOptionsReturnType[],
  value: string,
  label: typeof SUBSCRIPTION_PRICE_TYPE_MAP[SubscriptionData['prices'][number]['type']]['label'],
}


export const subscriptionPlansToSelectOptions = ({
  currency,
  subscription,
  startDate,
  timeZone,
}: {
  currency: CurrenciesEnum,
  subscription: Subscription | SubscriptionData,
  startDate?: number | null,
  timeZone: string,
}) =>
  (
    subscription.prices.reduce<CycleOption[]>((acc, plan) => {
      let startDateValue: DateTime<boolean> = DateTime.utc().startOf('day')
      // Convert `startDate` to `DateTime<boolean>` if it's a string or number
      if (typeof startDate === 'string') {
        startDateValue = DateTime.fromISO(startDate) as DateTime<boolean>
      } else if (typeof startDate === 'number') {
        startDateValue = DateTime.fromMillis(startDate) as DateTime<boolean>
      }
      // Cycles always go out at LIST price; with a code the server discounts them (#2835).
      const cycles = generateOptionPerType({
        ...subscription,
        prices: subscription.prices.map((price) => ({...price, price: price.listPrice ?? price.price})),
        timeZone,
        startDate: startDateValue,
        specificStartDate: subscription.startDate,
        durationFromSubscription: subscription.duration,
        priceTime: plan.type,
        durationTypeFromSubscription: subscription.durationType,
      })
      const {totalPrice, selectedCycles, availableCyclesAmount} = getPayAmountWithSelectedCycles(cycles, plan)
      const maxInstallments = plan.maxInstallments
      const installmentsOptions: InstOptionsReturnType[] = []
      if (
        maxInstallments
        && plan.type === SubscriptionPriceTypesEnum.ONE_TIME
        && totalPrice > 0
      ) {
        installmentsOptions.push(
          ...(
            generateSubscriptionInstallmentsOptions({
              currency,
              totalPrice,
              maxInstallments
            })
          )
        )
      }
      const temp: CycleOption = {
        plan,
        cycles,
        type: plan.type,
        totalPrice,
        selectedCycles,
        availableCyclesAmount,
        installmentsOptions,
        value: String(plan.price),
        label: SUBSCRIPTION_PRICE_TYPE_MAP[plan.type].label,
      }
      return [...acc, temp]
    }, [])
  )

